"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import type {
  CreateBusinessRequestBody,
  CreateBusinessResponse,
} from "@/app/api/business/create/route";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useSession } from "@/lib/auth/client";
import { useOrganization } from "@/lib/organizationContext";

// Simple schema for initial business creation (only business name)
const BusinessFormSchema = z.object({
  businessName: z
    .string()
    .min(2, "Business name must be at least 2 characters")
    .max(100, "Business name must be no more than 100 characters"),
});

type BusinessFormData = z.infer<typeof BusinessFormSchema>;

interface BusinessFormProps {
  onSuccess?: (businessId: string) => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

export function BusinessForm({
  onSuccess,
  onCancel,
  isLoading = false,
}: BusinessFormProps) {
  const { data: session } = useSession();
  const user = session?.user;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActiveOrganizationId } = useOrganization();

  const form = useForm<BusinessFormData>({
    resolver: zodResolver(BusinessFormSchema),
    defaultValues: {
      businessName: "",
    },
  });

  const onSubmit = async (data: BusinessFormData) => {
    if (!user?.id) {
      toast("No user ID found - please sign in");
      return;
    }

    setIsSubmitting(true);

    try {
      // Create Organization → Member → Business records via API route
      // This implements the Organization Plugin pattern (ADR-002)
      // API route verifies authentication and uses session.user.id for security
      // TODO: Make this a server action!
      const response = await fetch("/api/business/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessName: data.businessName,
        } satisfies CreateBusinessRequestBody),
      });

      const result: CreateBusinessResponse = await response.json();

      // Check HTTP status code for proper error handling
      if (!response.ok) {
        if (response.status === 401) {
          toast("Session expired. Please sign in.");
          return;
        }
        throw new Error(result.error || "Failed to create business");
      }

      if (!result.success || !result.organizationId) {
        throw new Error(result.error || "Failed to create business");
      }

      toast(`Business "${data.businessName}" created successfully!`);

      await setActiveOrganizationId(result.organizationId);

      // Call success callback
      if (onSuccess && result.businessId) {
        onSuccess(result.businessId);
      }
    } catch (error) {
      console.error("Error creating business:", error);
      toast("Failed to create business. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="businessName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Business name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter a business name"
                    {...field}
                    disabled={isLoading || isSubmitting}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-3">
            {onCancel && (
              <Button
                type="button"
                variant="link"
                onClick={onCancel}
                disabled={isLoading || isSubmitting}
              >
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isLoading || isSubmitting}>
              {(isLoading || isSubmitting) && <Spinner className="mr-2" />}
              Create business
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
